/**
 * The statistics Chart Analytica shares across its fifteen dimensions.
 *
 * <h2>Everything degenerate answers NULL, never NaN and never Infinity</h2>
 *
 * This is the rule the rest of this repository already follows for profit factor and for the
 * institutional ratios, and the reason is the same: `Infinity` survives `.toFixed()` and renders
 * as a spectacular score, and `NaN` renders as the word NaN in a cell a reader will take for a
 * measurement. A dimension that cannot answer must say so, and the dashboard prints `n/a`.
 *
 * <p>A null is therefore NOT a zero. "No section reached the third boundary" is a count of zero;
 * "there were no sections to ask about" is a null. Collapsing the two would let an empty scan
 * report a chart full of structure that never happened.
 */

/** A finite number, or null. The one coercion every function here starts from. */
export function num(v) {
  return (typeof v === 'number' && Number.isFinite(v)) ? v : null;
}

/** Arithmetic mean of the finite entries, or null when there are none. */
export function mean(values) {
  const xs = (Array.isArray(values) ? values : []).map(num).filter(v => v !== null);
  if (xs.length === 0) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

/** Sample standard deviation (n-1), or null below two observations. */
export function stdev(values) {
  const xs = (Array.isArray(values) ? values : []).map(num).filter(v => v !== null);
  if (xs.length < 2) return null;
  const m = xs.reduce((a, b) => a + b, 0) / xs.length;
  const ss = xs.reduce((a, b) => a + (b - m) * (b - m), 0);
  return Math.sqrt(ss / (xs.length - 1));
}

/** The median, or null when empty. Even counts take the midpoint of the two centre values. */
export function median(values) {
  const xs = (Array.isArray(values) ? values : []).map(num).filter(v => v !== null)
    .sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = xs.length >> 1;
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

/**
 * A hit rate as a FRACTION, with its denominator carried alongside.
 *
 * <p>The denominator travels because a rate without one is unreadable: 100% of one observation and
 * 100% of two hundred are the same number and completely different findings. Every table in the
 * dashboard prints `n` beside the percentage for that reason.
 *
 * <p>`n === 0` gives a null rate rather than 0, because nothing was tried.
 */
export function rate(hits, total) {
  const h = num(hits);
  const t = num(total);
  if (h === null || t === null || t <= 0) return { hits: h || 0, total: t || 0, rate: null };
  return { hits: h, total: t, rate: h / t };
}

/**
 * PEARSON's correlation coefficient between two aligned series.
 *
 * <p>Null below three pairs, and null when either series has no variance at all — a vertical or
 * horizontal scatter has no correlation to report, and the formula's denominator is zero there.
 * Returning 0 instead would say "measured, and found unrelated", which is a different and much
 * stronger claim than "could not be measured".
 *
 * @returns {{r:number|null, n:number}}
 */
export function pearson(xsIn, ysIn) {
  const xs = [];
  const ys = [];
  const a = Array.isArray(xsIn) ? xsIn : [];
  const b = Array.isArray(ysIn) ? ysIn : [];
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const x = num(a[i]);
    const y = num(b[i]);
    if (x !== null && y !== null) { xs.push(x); ys.push(y); }
  }
  const n = xs.length;
  if (n < 3) return { r: null, n };

  const mx = xs.reduce((p, q) => p + q, 0) / n;
  const my = ys.reduce((p, q) => p + q, 0) / n;
  let sxy = 0; let sxx = 0; let syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - mx;
    const dy = ys[i] - my;
    sxy += dx * dy; sxx += dx * dx; syy += dy * dy;
  }
  if (sxx <= 0 || syy <= 0) return { r: null, n };
  return { r: sxy / Math.sqrt(sxx * syy), n };
}

/**
 * How a correlation coefficient should be READ, in words.
 *
 * <p>A bare `r = 0.31` invites two opposite misreadings — "a third of the variance" (it is not;
 * that is r²) and "no relationship" — so the dashboard prints the adjective beside the number.
 * The bands are the conventional ones and the sign is carried separately by the number itself.
 */
export function describeCorrelation(r) {
  const v = num(r);
  if (v === null) return 'not measurable';
  const a = Math.abs(v);
  const strength = a >= 0.7 ? 'strong' : a >= 0.4 ? 'moderate' : a >= 0.2 ? 'weak' : 'negligible';
  if (strength === 'negligible') return 'negligible';
  return `${strength} ${v > 0 ? 'positive' : 'negative'}`;
}

/**
 * The WILSON score interval for a proportion — the honest error bar on a hit rate.
 *
 * <p>Wilson rather than the textbook normal approximation, because the normal one is wrong in
 * exactly the places these tables live: it gives intervals reaching below 0 or above 1 on small
 * samples and collapses to a zero-width interval at 0% and 100%, which reads as certainty from
 * three observations. Wilson stays inside [0, 1] and keeps a width at the extremes.
 *
 * <p>95% by default (z = 1.96). Null for an empty sample.
 *
 * @returns {{low:number, high:number}|null}
 */
export function wilson(hits, total, z = 1.96) {
  const h = num(hits);
  const t = num(total);
  if (h === null || t === null || t <= 0) return null;
  const p = h / t;
  const z2 = z * z;
  const denom = 1 + z2 / t;
  const centre = p + z2 / (2 * t);
  const spread = z * Math.sqrt((p * (1 - p) + z2 / (4 * t)) / t);
  return {
    low: Math.max(0, (centre - spread) / denom),
    high: Math.min(1, (centre + spread) / denom),
  };
}

/**
 * Does a hit rate CLEAR a baseline, or is it inside the noise?
 *
 * <p>The question every "success rate" column in this dashboard has to survive. A 62% success rate
 * on eight observations is not evidence of anything, and printing it beside a 58% on four hundred
 * invites a reader to prefer the first. This answers with the Wilson interval rather than with a
 * p-value because an interval also says how much room there is to be wrong.
 *
 * @returns {'above'|'below'|'inconclusive'|null}
 */
export function versusBaseline(hits, total, baseline) {
  const ci = wilson(hits, total);
  const base = num(baseline);
  if (!ci || base === null) return null;
  if (ci.low > base) return 'above';
  if (ci.high < base) return 'below';
  return 'inconclusive';
}

/**
 * Drops a value into the first bucket whose upper EDGE it is below, the last bucket catching the
 * rest.
 *
 * <p>Edges are EXCLUSIVE upper bounds and the comparison is `<`, so a value sitting exactly on an
 * edge goes UP into the next bucket. That is the convention the band dimensions need: "achieved a
 * 2x move" should include a move of exactly 2x, and the alternative silently demotes every
 * boundary touch to the tier below it.
 *
 * @returns {number} the bucket index, 0..edges.length
 */
export function bucketIndex(value, edges) {
  const v = num(value);
  if (v === null) return -1;
  const es = Array.isArray(edges) ? edges : [];
  for (let i = 0; i < es.length; i++) {
    if (v < es[i]) return i;
  }
  return es.length;
}

/** A percentage string with one decimal, or `n/a`. The one place rounding is decided. */
export function pct(fraction, decimals = 1) {
  const v = num(fraction);
  if (v === null) return 'n/a';
  return `${(v * 100).toFixed(decimals)}%`;
}

/** A plain number with fixed decimals, or `n/a`. */
export function fixed(value, decimals = 2) {
  const v = num(value);
  if (v === null) return 'n/a';
  return v.toFixed(decimals);
}
